import { createHash } from "node:crypto";
import { envValue, type EnvRecord } from "./envPreflight";
import { DAY_MS, HOURS } from "./window";

/**
 * X1 AUTH policy: every number and every pure decision the actor layer makes,
 * in one place so a reviewer reads the whole surface at once and the web, the
 * CLI and the tests share it. No IO.
 *
 * Every number here is invented by the design (DESIGN-interface section X1 and
 * its open question 2) and carries an AWAITING RATIFICATION marker; none is
 * Kabir's yet. The marker is held in place by authPolicy.test.ts.
 */

/** 12 h idle (design open question 2). AWAITING RATIFICATION */
export const SESSION_IDLE_MS = 12 * HOURS; // AWAITING RATIFICATION
/** 7 days absolute (design open question 2). AWAITING RATIFICATION */
export const SESSION_ABSOLUTE_MS = 7 * DAY_MS; // AWAITING RATIFICATION
/** Failed logins per email inside the window before it is locked (design X1 throttle). AWAITING RATIFICATION */
export const LOGIN_FAIL_LIMIT_PER_EMAIL = 5; // AWAITING RATIFICATION
/** Failed logins per source address inside the window (design X1 throttle). AWAITING RATIFICATION */
export const LOGIN_FAIL_LIMIT_PER_IP = 20; // AWAITING RATIFICATION
/**
 * Attempts per window, from callers whose address cannot be trusted (the proxy-header flag is off), at which
 * ONE loud owner alert is raised. It is a volume alarm, never a limit: a shared bucket that refuses lets 200
 * junk logins lock the owner out (fix round 2). The per-email and per-2FA buckets are the throttle. AWAITING RATIFICATION
 */
export const LOGIN_UNRESOLVED_ALERT_THRESHOLD = 200; // AWAITING RATIFICATION
/** A session that has passed the password but not the second factor lives this long, idle and absolute (fix round 1, item 6). AWAITING RATIFICATION */
export const PRE_MFA_SESSION_MS = 10 * 60 * 1000; // AWAITING RATIFICATION
/** The throttle window, 15 minutes (design X1 throttle). AWAITING RATIFICATION */
export const LOGIN_FAIL_WINDOW_MS = 15 * 60 * 1000; // AWAITING RATIFICATION
/** An owner-issued invite link lives 72 hours (X1 assumption, TA-style). AWAITING RATIFICATION */
export const INVITE_TTL_MS = 72 * HOURS; // AWAITING RATIFICATION
/** An owner-issued reset link lives 1 hour (X1 assumption, open question 6). AWAITING RATIFICATION */
export const RESET_TTL_MS = 1 * HOURS; // AWAITING RATIFICATION
/** One-time recovery codes issued at 2FA enrolment (X1 assumption). AWAITING RATIFICATION */
export const RECOVERY_CODE_COUNT = 10; // AWAITING RATIFICATION
/** Shortest accepted password, NIST 800-63B style: length, not composition (X1 assumption). AWAITING RATIFICATION */
export const PASSWORD_MIN_LENGTH = 12; // AWAITING RATIFICATION
/** Longest accepted password: scrypt cost grows with input, so an unbounded one is a denial of service (X1 assumption). AWAITING RATIFICATION */
export const PASSWORD_MAX_LENGTH = 256; // AWAITING RATIFICATION

export interface SessionClocks {
  readonly createdAt: Date;
  readonly lastSeenAt: Date;
  readonly idleExpiresAt: Date;
  readonly absoluteExpiresAt: Date;
  readonly revokedAt: Date | null;
}

export type SessionVerdict =
  | { readonly live: true }
  | { readonly live: false; readonly reason: "revoked" | "idle" | "absolute" };

/** Revoked beats absolute beats idle: the strongest reason is the one reported. */
export function sessionVerdict(s: SessionClocks, now: Date): SessionVerdict {
  if (s.revokedAt !== null) return { live: false, reason: "revoked" };
  if (now.getTime() >= s.absoluteExpiresAt.getTime()) return { live: false, reason: "absolute" };
  if (now.getTime() >= s.idleExpiresAt.getTime()) return { live: false, reason: "idle" };
  return { live: true };
}

/** The idle expiry a touch at `now` sets: never later than the absolute one. */
export function nextIdleExpiry(now: Date, absoluteExpiresAt: Date): Date {
  return new Date(Math.min(now.getTime() + SESSION_IDLE_MS, absoluteExpiresAt.getTime()));
}

export function isLoginAllowed(counts: { readonly emailFailures: number; readonly ipFailures: number }): boolean {
  return counts.emailFailures < LOGIN_FAIL_LIMIT_PER_EMAIL && counts.ipFailures < LOGIN_FAIL_LIMIT_PER_IP;
}

export type PasswordCheck = { readonly ok: true } | { readonly ok: false; readonly reason: string };

export function checkPasswordPolicy(pw: string): PasswordCheck {
  if (pw.trim().length === 0) return { ok: false, reason: "The password cannot be blank." };
  if (pw.length < PASSWORD_MIN_LENGTH) {
    return { ok: false, reason: `Use at least ${String(PASSWORD_MIN_LENGTH)} characters. A short sentence works well.` };
  }
  if (pw.length > PASSWORD_MAX_LENGTH) {
    return { ok: false, reason: `Use at most ${String(PASSWORD_MAX_LENGTH)} characters.` };
  }
  return { ok: true };
}

/**
 * Deploy flag (fix round 1, item 3; restated Z3 XFF, decision 374, OPEN row 323): believe a hop of
 * x-forwarded-for only when this server sits behind a proxy that APPENDS the connecting address to it (an
 * AWS ALB does). The hop believed is the one that proxy appended: counted from the RIGHT, never the left,
 * because the left is whatever the caller wrote. Default OFF.
 * Which setting a deploy uses is AWAITING RATIFICATION and flipping it is Kabir's.
 */
export const TRUST_PROXY_HEADERS_ENV = "ACKRA_TRUST_PROXY_HEADERS";

/**
 * How many trusted proxies sit in front of the web server, so which value from the right is the caller.
 * Unset is 1: a topology fact, one ALB with no CloudFront in front (deploy/aws/alb.tf, append pinned by
 * deployPosture F1b), not a tuned number. A value that is not an integer >= 1 is a config
 * typo: the resolver then reads nothing for every request (unresolved, never refused) and the web boot warns.
 */
export const TRUST_PROXY_HOPS_ENV = "ACKRA_TRUST_PROXY_HOPS";

export function trustsProxyHeaders(env: EnvRecord): boolean {
  const v = envValue(env, TRUST_PROXY_HEADERS_ENV)?.toLowerCase();
  return v === "1" || v === "true";
}

/** The configured hop count, 1 when unset, or null when the variable is set to anything but an integer >= 1. */
export function trustedProxyHops(env: EnvRecord): number | null {
  const v = envValue(env, TRUST_PROXY_HOPS_ENV);
  if (v === undefined) return 1;
  if (!/^[1-9][0-9]{0,2}$/.test(v)) return null;
  return Number(v);
}

/**
 * The caller's address, or null when it cannot be trusted or is absent. With trust on, the `hops`-th value
 * from the RIGHT of x-forwarded-for (1 = the value the trusted proxy appended). Fewer values than hops, an
 * empty or over-long value, or a bad hop count is null, never the leftmost. Null is throttled as one shared
 * bucket, never skipped and never refused.
 */
export function clientIpFromHeaders(
  get: (name: string) => string | null | undefined,
  trust: boolean,
  hops: number | null = 1,
): string | null {
  if (!trust) return null;
  if (hops === null || !Number.isInteger(hops) || hops < 1) return null;
  const values = get("x-forwarded-for")?.split(",").map((v) => v.trim());
  if (values === undefined || values.length < hops) return null;
  const hop = values[values.length - hops];
  return hop !== undefined && hop !== "" && hop.length <= 64 ? hop : null;
}

/**
 * Throttle bucket keys (fix round 1, item 1). A bucket is one counter row, charged BEFORE the secret is
 * checked and refunded on success, so parallel requests cannot all pass a count taken before any of them
 * recorded. Password and second-factor buckets are separate: a person who knows the password must not be
 * able to refill the code guesses by logging in again. The address is never stored in the email key.
 */
export const THROTTLE_UNRESOLVED_KEY = "ip:unresolved";
export const emailThrottleKey = (email: string): string => `pw:${createHash("sha256").update(email.trim().toLowerCase()).digest("hex")}`;
export const secondFactorThrottleKey = (userId: string): string => `2fa:${userId}`;
export const ipThrottleKey = (ip: string | null): string => (ip === null ? THROTTLE_UNRESOLVED_KEY : `ip:${ip}`);

export function throttleLimit(key: string): number {
  // Counted for the volume alert, never refused: one shared bucket would let strangers lock out everyone.
  if (key === THROTTLE_UNRESOLVED_KEY) return Number.POSITIVE_INFINITY;
  return key.startsWith("ip:") ? LOGIN_FAIL_LIMIT_PER_IP : LOGIN_FAIL_LIMIT_PER_EMAIL;
}

/**
 * True when this process is a production one that does not trust proxy headers: every caller is then an
 * unresolved address and the per-address limit cannot work. The web boot logs a loud warning on it (fix round 2).
 */
export function isProductionWithoutProxyTrust(env: EnvRecord): boolean {
  return envValue(env, "NODE_ENV") === "production" && !trustsProxyHeaders(env);
}
